
import React, { useState, useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { styled } from '@mui/system';

const theme = createTheme();

const TickerSymbol = styled('span')(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    zIndex: 10, // above chart, below menu card overlays
}));

export const ChartComponent = (props) => {

    const {
        tickerSymbol,
        candleSeriesData,
        volumeSeriesData,
        candleColors: {
            upColor = '#26a69a',
            downColor = '#ef5350',
        } = {},
        volumeColors: {
            color = '#26a69a',
        } = {},
        textColor = 'black'
    } = props;

    const chartContainerRef = useRef();
    const tickerRef = useRef();

    const minHeight = 200;
    const dynamicHeight = window.innerHeight * 0.9;
    const chartHeight = dynamicHeight > minHeight ? dynamicHeight : minHeight;

    useEffect(() => {
        if (chartContainerRef.current) {
            const handleResize = () => {
                chart.applyOptions({ width: chartContainerRef.current.clientWidth });
            };

            const chart = createChart(chartContainerRef.current, {
                layout: {
                    textColor: textColor,
                },
                width: chartContainerRef.current.clientWidth,
                height: chartHeight,
                handleScroll: false,
                handleScale: false,
            });

            const candlestickSeries = chart.addCandlestickSeries({
                borderVisible: false,
                wickUpColor: upColor,
                wickDownColor: downColor,
            });

            const volumeSeries = chart.addHistogramSeries({
                color: color,
                priceFormat: {
                    type: "volume"
                },
                priceScaleId: "", 
                scaleMargins: {
                    top: 0.7,
                    bottom: 0
                }
            });
            volumeSeries.priceScale().applyOptions({
                scaleMargins: {
                    top: 0.7,
                    bottom: 0
                }
            });

            if (Array.isArray(candleSeriesData)) {
                const formattedCandleSeriesData = candleSeriesData.map(dataPoint => ({
                    time: dataPoint.time,
                    open: dataPoint.open,
                    high: dataPoint.high,
                    low: dataPoint.low,
                    close: dataPoint.close,
                    color: dataPoint.color || (dataPoint.close >= dataPoint.open ? upColor : downColor),
                    wickColor: dataPoint.color || (dataPoint.close >= dataPoint.open ? upColor : downColor)
                }));
            
                candlestickSeries.setData(formattedCandleSeriesData);
            }
            volumeSeries.setData(volumeSeriesData);

            chart.timeScale().fitContent();

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                chart.remove();
            };
        }
    }, [upColor, downColor, color, candleSeriesData, volumeSeriesData, textColor]);

    useEffect(() => {
        if (tickerRef.current) {
            tickerRef.current.innerText = tickerSymbol;
        }
    }, [tickerSymbol]);

    return (
    <div
        style={{
        position: "relative",
        width: "100%",      // fill parent
        height: "100%",     // fill parent
        margin: 0,
        padding: 0,
        overflow: "hidden",
        }}
    >
        <div
        ref={chartContainerRef}
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            userSelect: "none",
            margin: 0,
            padding: 0,
        }}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        />
        <ThemeProvider theme={theme}>
        <TickerSymbol>{tickerSymbol}</TickerSymbol>
        </ThemeProvider>
    </div>
    );
};
